import { Helmet } from 'react-helmet-async';
import { useApp } from '../context.jsx';

const BRAND = 'Елизавета Федькина';
const BRAND_EN = 'Elizaveta Fedkina';
const SITE = (import.meta.env.VITE_SITE_URL || 'https://liza-brand-os.pages.dev').replace(/\/$/, '');
const DEFAULT_IMAGE = `${SITE}/paintings/portal.webp`;

/**
 * Единая обёртка над <Helmet>. Одного места хватит на все 11 маршрутов.
 *
 * @param {Object} p
 * @param {string} p.title         — без «— бренд», добавлю сам
 * @param {string} p.description   — 140-160 символов
 * @param {string} [p.path]        — относительный путь, напр. "/artworks/alisa" (для canonical + og:url)
 * @param {string} [p.image]       — абсолютный или относительный URL обложки
 * @param {'website'|'article'} [p.type]
 * @param {string} [p.publishedAt] — ISO date для article
 * @param {Array<Object>} [p.jsonLd] — дополнительные schema.org блоки (Product, Article, BreadcrumbList, ...)
 */
export default function Seo({ title, description, path = '', image, type = 'website', publishedAt, jsonLd }) {
  const { lang } = useApp();
  const brand = lang === 'ru' ? BRAND : BRAND_EN;
  const fullTitle = title ? `${title} — ${brand}` : brand;
  const url = path ? `${SITE}${path.startsWith('/') ? path : '/' + path}` : SITE;
  const ogImage = image
    ? (image.startsWith('http') ? image : `${SITE}${image.startsWith('/') ? image : '/' + image}`)
    : DEFAULT_IMAGE;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />

      {/* hreflang для индексации EN-версии международными поисковиками */}
      <link rel="alternate" hrefLang="ru"        href={`${url}${url.includes('?') ? '&' : '?'}lang=ru`} />
      <link rel="alternate" hrefLang="en"        href={`${url}${url.includes('?') ? '&' : '?'}lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      <meta property="og:type"        content={type} />
      <meta property="og:site_name"   content={brand} />
      <meta property="og:title"       content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url"         content={url} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:locale"      content={lang === 'ru' ? 'ru_RU' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'ru' ? 'en_US' : 'ru_RU'} />

      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image"       content={ogImage} />

      {type === 'article' && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}

      {Array.isArray(jsonLd) && jsonLd.filter(Boolean).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
